import { ElementRef, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import html2canvas from 'html2canvas';
import * as jspdf from 'jspdf';
import { BehaviorSubject, finalize, from, Observable, tap } from 'rxjs';
import { CommonCommunicationService } from './commonCommunication.service';

import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class CommonUtilityService {
  constructor(
    private toastrService: ToastrService,
    private communicationService: CommonCommunicationService,
    private translateService: TranslateService
  ) {}

  possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  listingInsightPdfBlob = new BehaviorSubject<any>(null);
  salesListingPdfBlob = new BehaviorSubject<any>(null);
  setModulePermission(moduleList: any, moduleType: any) {
    let permission = moduleList;
    let modulePermission: any = {
      is_delete: false,
      is_write: false,
      is_update: false,
      is_view: false
    };
    permission = permission.filter((item: any) => item?.moduleName === moduleType);

    permission.forEach((item: any) => {
      if (!modulePermission.is_delete) {
        modulePermission.is_delete = item.is_delete;
      }
      if (!modulePermission.is_update) {
        modulePermission.is_update = item.is_update;
      }
      if (!modulePermission.is_view) {
        modulePermission.is_view = item.is_view;
      }
      if (!modulePermission.is_write) {
        modulePermission.is_write = item.is_write;
      }
    });

    return modulePermission;
  }

  validNumber(event: any) {
    var charCode = event.which ? event.which : event.keyCode;

    if ((charCode < 48 || charCode > 57) && charCode != 46) {
      event.preventDefault();
      return false;
    } else {
      return true;
    }
  }
  validChar(event: any) {
    const char = event.key;

    if (/[0-9]/.test(char)) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  generateCode(length: any) {
    let text = '';

    for (let i = 0; i < length; i++) {
      text += this.possible.charAt(Math.floor(Math.random() * this.possible.length));
    }
    return text.toUpperCase();
  }

  showSuccess(message: any, title?: any) {
    const translatedMessage = this.translateService.instant(message);
    const translatedTitle = title ? this.translateService.instant(title) : title;
    this.toastrService.show(translatedMessage, translatedTitle, {
      closeButton: true,
      newestOnTop: true,
      positionClass: 'toast-top-right',
      timeOut: 5000,
      extendedTimeOut: 1000,
      toastClass: 'ngx-toastr ngx-toastr-success'
    });
  }

  showDanger(message: any, title?: any) {
    const translatedMessage = this.translateService.instant(message);
    const translatedTitle = title ? this.translateService.instant(title) : '';
    this.toastrService.show(translatedMessage, translatedTitle, {
      closeButton: true,
      newestOnTop: true,
      positionClass: 'toast-top-right',
      timeOut: 3000,
      extendedTimeOut: 1000,
      toastClass: 'ngx-toastr ngx-toastr-danger'
    });
  }

  // amber "heads up" toast — used for non-blocking advisories (e.g. a large but accepted upload)
  showWarning(message: any, title?: any) {
    const translatedMessage = this.translateService.instant(message);
    const translatedTitle = title ? this.translateService.instant(title) : '';
    this.toastrService.show(translatedMessage, translatedTitle, {
      closeButton: true,
      newestOnTop: true,
      positionClass: 'toast-top-right',
      timeOut: 6000,
      extendedTimeOut: 1500,
      toastClass: 'ngx-toastr toast-warning'
    });
  }

  inputValidation(data: any) {
    data = data || '';
    let text = data.toString();

    if (!text.length) {
      return false;
    }
    return true;
  }

  emailValidation(input: any) {
    var validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})?$/;

    if (input.match(validRegex)) {
      return true;
    } else {
      return false;
    }
  }

  setCanvasWidth(width: number) {
    let canvasWidth = 0;
    if (width > 1222) {
      canvasWidth = 1222;
    } else if (width > 750) {
      canvasWidth = 750;
    } else {
      canvasWidth = width;
    }
    return canvasWidth;
  }

  convertHtmlToPdf(reportDetails: { event: ElementRef; filename: string }) {
    return from(
      html2canvas(reportDetails.event.nativeElement).then((canvas) => {
        const contentDataURL = canvas.toDataURL('image/png');
        const pdf = new jspdf.jsPDF({
          orientation: 'portrait',
          unit: 'px',
          format: [this.setCanvasWidth(canvas.width), 893]
        });
        const contentWidth = 730;
        const contentHeight = 893;
        pdf.addImage(contentDataURL, 'PNG', 0, 0, contentWidth, contentHeight);
        const output = pdf.output('blob');
        return { output: output, filename: reportDetails.filename };
      })
    );
  }

  adjustPageAfterDelete(page: any, pageSize: any, totalItems: any) {
    totalItems = totalItems - 1;
    const start = (page - 1) * pageSize;
    if (start >= totalItems && page > 1) {
      return page - 1;
    }
    return page;
  }

  convertToUTC(date: any) {
    return new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
      )
    );
  }
  checkMissingDetails(shopDetails: any, optionsObj?: any) {
    const ignoreAccountKeys = ['tin_no', 'cin', 'gst_number', 'aadhar_number', 'pan_number', 'fssaiNo'];
    const ignoreAddressKeys = ['longitude', 'latitude', 'pincode', 'tin_no'];

    const missing: any = {
      account_details: [],
      addresses: {
        billing: [],
        business: [],
        shipping: []
      },
      integrations: []
    };

    const account = shopDetails.account_details;
    Object.keys(account).forEach((key) => {
      if (ignoreAccountKeys.includes(key)) return;
      if (!account[key]) missing.account_details.push(key);
    });

    const addressCheck = (obj: any, targetList: string[]) => {
      Object.keys(obj).forEach((key) => {
        if (ignoreAddressKeys.includes(key)) return;
        if (!obj[key]) targetList.push(key);
      });
    };

    const billing = shopDetails.address?.billing_address;
    const business = shopDetails.address?.business_address;
    const shipping = shopDetails.address?.shipping_address;

    addressCheck(billing, missing.addresses.billing);
    addressCheck(business, missing.addresses.business);
    addressCheck(shipping, missing.addresses.shipping);

    const integrations = shopDetails.integrations;
    const paymentGateWay = shopDetails.paymentGatewayConfiguration;
    // revert it back once shiprocket is added
    // if (!integrations.isShippingAdded) missing.integrations.push("shiprocket");
    if (!paymentGateWay?.paymentGatewayName) missing.integrations.push('Payment Gateway');

    missing.taxNotFound = !optionsObj?.taxNotFound;

    this.communicationService.intialSetupData.next(missing);
    return missing;
  }

  checkShopSetup(shopDetails: any) {
    let isSetupCompleted = false;
    if (
      shopDetails.onboardingConfig.isKYCCompleted &&
      shopDetails.onboardingConfig.isPaymentConfigured &&
      shopDetails.onboardingConfig.isShippingConfigured &&
      shopDetails.onboardingConfig.isTaxConfigured &&
      shopDetails.onboardingConfig.isPickupConfigured
    ) {
      isSetupCompleted = true;
    }
    return isSetupCompleted;
  }

  dateFormate(date: any) {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }
  formatCurrency(
    value: number,
    options: {
      showSymbol?: boolean;
      abbreviated?: boolean;
      decimals?: number;
      currency?: string;
    } = {}
  ): string {
    const { showSymbol = true, abbreviated = false, decimals = 2, currency = 'INR' } = options;

    const normalizedCurrency = this.normalizeCurrency(currency);
    const isINR = normalizedCurrency === 'INR';
    const symbol = this.getCurrencySymbol(normalizedCurrency);

    let formattedNumber = '';
    let suffix = '';

    if (abbreviated) {
      if (isINR) {
        if (value >= 10000000) {
          formattedNumber = (value / 10000000).toFixed(1);
          suffix = 'Cr';
        } else if (value >= 100000) {
          formattedNumber = (value / 100000).toFixed(1);
          suffix = 'L';
        } else if (value >= 1000) {
          formattedNumber = (value / 1000).toFixed(1);
          suffix = 'K';
        } else {
          formattedNumber = value.toFixed(0);
        }
      } else {
        if (value >= 1_000_000_000) {
          formattedNumber = (value / 1_000_000_000).toFixed(1);
          suffix = 'B';
        } else if (value >= 1_000_000) {
          formattedNumber = (value / 1_000_000).toFixed(1);
          suffix = 'M';
        } else if (value >= 1000) {
          formattedNumber = (value / 1000).toFixed(1);
          suffix = 'K';
        } else {
          formattedNumber = value.toFixed(0);
        }
      }

      return showSymbol ? `${symbol} ${formattedNumber}${suffix}` : `${formattedNumber}${suffix}`;
    }
    if (isINR) {
      formattedNumber = value.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    } else {
      formattedNumber = value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    }

    return showSymbol ? `${symbol} ${formattedNumber}` : formattedNumber;
  }

  normalizeCurrency(currency: string = 'INR'): string {
    if (!currency) return 'INR';

    const clean = currency.trim();

    const map: Record<string, string> = {
      '₹': 'INR',
      $: 'USD',
      '€': 'EUR',
      '£': 'GBP',
      AED: 'AED'
    };

    return map[clean] || clean.toUpperCase();
  }

  getCurrencySymbol(currency: string): string {
    try {
      const parts = new Intl.NumberFormat('en', {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol'
      }).formatToParts(0);

      return parts.find((p) => p.type === 'currency')?.value || currency;
    } catch {
      return currency;
    }
  }

  getCurrencyUnitLabel(currency: string = 'INR'): string {
    const normalizedCurrency = this.normalizeCurrency(currency);
    const isINR = normalizedCurrency === 'INR';

    return isINR ? `ui_inventory_value_inr_crores_lbl` : `ui_inventory_value_usd_millions_lbl`;
  }

  observe<T>(config: { loading?: any; success?: any; error?: any }) {
    return (source: Observable<T>) => {
      let loadingToast: any = null;
      if (config.loading) {
        loadingToast = this.toastrService.show(config.loading, '', {
          closeButton: true,
          newestOnTop: true,
          positionClass: 'toast-top-right',
          timeOut: 3000,
          extendedTimeOut: 1000,
          toastClass: 'ngx-toastr toast-info'
        });
      }
      const closeLoading = () => {
        if (loadingToast) {
          try {
            loadingToast.toastRef.manualClose();
          } catch {}
          loadingToast = null;
        }
      };
      return source.pipe(
        finalize(() => closeLoading()),
        tap({
          next: () => {
            closeLoading();
            if (config.success) this.showSuccess(config.success);
          },
          error: (err: any) => {
            closeLoading();
            if (config.error != null) {
              // config.error may be a static message OR a function that derives the message from the
              // actual error (e.g. surfacing the server's "Image size exceeds 2MB" text). Call it with
              // the error; otherwise showDanger would stringify the function and toast its source code.
              const msg = typeof config.error === 'function' ? config.error(err) : config.error;
              if (msg) this.showDanger(msg);
            }
          }
        })
      );
    };
  }
}
